import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Briefcase, Plus, Pencil, Trash2, Power } from "lucide-react";
import saApi from "../../superadminApi";
import { DdApiKeysPanel } from "../../components/dd/DdApiKeysPanel";

const inputCls = "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-[#ff7e00] placeholder:text-gray-500";

const ConsultantForm = ({ value, companies, onCancel, onSaved }) => {
  const [f, setF] = useState(value);
  const toggle = (code) => setF({ ...f, companies: f.companies.includes(code) ? f.companies.filter((c) => c !== code) : [...f.companies, code] });
  const save = async () => {
    try {
      if (f.id) await saApi.put(`/superadmin/consultants/${f.id}`, { name: f.name, companies: f.companies, password: f.password || undefined });
      else await saApi.post("/superadmin/consultants", f);
      toast.success("Consulente salvato"); onSaved();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Salvataggio non riuscito"); }
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-3 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="sa-consultant-form">
      <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nome e cognome" data-testid="sa-consultant-name" className={inputCls} />
      <input value={f.email} disabled={!!f.id} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="sa-consultant-email" className={`${inputCls} disabled:opacity-50`} />
      <input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder={f.id ? "Nuova password (facoltativa)" : "Password (min 8)"} data-testid="sa-consultant-password" className={inputCls} />
      <div className="sm:col-span-3">
        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Aziende clienti abilitate</div>
        <div className="flex flex-wrap gap-2">
          {companies.map((c) => (
            <label key={c.code} className={`flex items-center gap-1.5 text-[12.5px] font-semibold px-2.5 py-1 rounded-full border cursor-pointer ${f.companies.includes(c.code) ? "border-[#ff7e00] text-white bg-[#ff7e00]/15" : "border-white/15 text-gray-300"}`}>
              <input type="checkbox" checked={f.companies.includes(c.code)} onChange={() => toggle(c.code)} data-testid={`sa-consultant-company-${c.code}`} className="accent-[#ff7e00]" /> {c.label}
            </label>
          ))}
        </div>
      </div>
      <div className="sm:col-span-3 flex justify-end gap-2">
        <button onClick={onCancel} className="text-gray-300 text-[13px] font-semibold px-3 py-1.5">Annulla</button>
        <button onClick={save} data-testid="sa-consultant-save" className="bg-[#ff7e00] hover:bg-[#e86f00] text-white text-[13px] font-bold px-4 py-1.5 rounded-lg">Salva</button>
      </div>
    </div>
  );
};

const ApiKeysBlock = ({ companies }) => {
  const [picked, setCode] = useState("");
  if (!companies.length) return null;
  const code = picked || companies[0].code;
  return (
    <div className="mt-4" data-testid="sa-dd-api-keys">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-bold text-gray-300">Chiavi API Due Diligence per</span>
        <select value={code} onChange={(e) => setCode(e.target.value)} data-testid="sa-dd-api-company" className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-[13px] text-white">
          {companies.map((c) => <option key={c.code} value={c.code} className="text-black">{c.label}</option>)}
        </select>
      </div>
      <DdApiKeysPanel key={code} client={saApi} keysPath={`/superadmin/companies/${code}/dd-api-keys`} company={code} dark />
    </div>
  );
};

// Console Superadmin: consulenti del lavoro (accesso multi-azienda) + chiavi API Due Diligence per azienda.
export const ConsultantsPanel = ({ companies }) => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => saApi.get("/superadmin/consultants").then((r) => setList(r.data.consultants || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const opts = companies.filter((c) => c.active !== false).map((c) => ({ code: c.code, label: c.label }));

  const toggleActive = async (c) => {
    await saApi.put(`/superadmin/consultants/${c.id}`, { active: !c.active }); load();
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare il consulente ${c.name}?`)) return;
    await saApi.delete(`/superadmin/consultants/${c.id}`); toast.success("Consulente eliminato"); load();
  };

  return (
    <div className="mb-10" data-testid="sa-consultants-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white text-[18px] font-black flex items-center gap-2"><Briefcase size={18} className="text-[#ff7e00]" /> Consulenti del lavoro</h2>
        <button onClick={() => setEditing({ name: "", email: "", password: "", companies: [] })} data-testid="sa-new-consultant-btn"
          className="flex items-center gap-2 border border-white/15 hover:border-white/40 text-gray-200 text-[13px] font-bold px-4 py-2 rounded-lg transition-colors"><Plus size={16} /> Nuovo consulente</button>
      </div>
      {editing && <ConsultantForm key={editing.id || "new"} value={editing} companies={opts} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-2" data-testid="sa-consultants-list">
        {list.length === 0 && <p className="px-4 py-3 text-[13px] text-gray-500">Nessun consulente. Accedono da <span className="font-mono">/consulente/login</span>.</p>}
        {list.map((c) => (
          <div key={c.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-white/5" data-testid={`sa-consultant-${c.id}`}>
            <span className="w-8 h-8 rounded-lg bg-[#ff7e00]/15 flex items-center justify-center text-[#ff7e00]"><Briefcase size={15} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-white text-[13px] font-bold">{c.name} {!c.active && <span className="text-[10px] bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded-full">Disattivato</span>}</div>
              <div className="text-gray-500 text-[11px] truncate">{c.email} · {(c.companies_detail || []).map((x) => x.label).join(", ") || "nessuna azienda"}</div>
            </div>
            <button title="Modifica" onClick={() => setEditing({ ...c, password: "" })} data-testid={`sa-consultant-edit-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><Pencil size={15} /></button>
            <button title={c.active ? "Disattiva" : "Riattiva"} onClick={() => toggleActive(c)} data-testid={`sa-consultant-toggle-${c.id}`} className="p-1.5 rounded-lg text-gray-300 hover:text-white hover:bg-white/10"><Power size={15} /></button>
            <button title="Elimina" onClick={() => remove(c)} data-testid={`sa-consultant-delete-${c.id}`} className="p-1.5 rounded-lg text-red-300 hover:bg-red-500/10"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      <ApiKeysBlock companies={opts} />
    </div>
  );
};

export default ConsultantsPanel;
